import { useEffect, useRef, useCallback, useState, useMemo, type MutableRefObject } from 'react';
import type { AxonEvent } from '@reflex/shared';
import type {
  ChatItem,
  ChatMessage,
  SetupProgressItem,
  AvailableCommand,
  TaskBlock,
  ToolCallBlock,
  ToolCallStatus,
  TurnBlock,
} from './types.js';
import { createBlockManager, useBlockManager, type BlockManager } from './useBlockManager.js';
import {
  classifyEvent,
  extractUserText,
  extractUserImageAttachments,
  extractFileEnvelopeAttachments,
  buildAgentConfigItem,
  buildAgentLifecycleItem,
  isUserMessageEvent,
} from './timeline-helpers.js';
import { runtimeSettingsSwitch } from './active-model.js';
import { handleClaudeProtocolEvent, parseControlResponse } from './claude-handler.js';
import { handleAcpProtocolEvent } from './acp-handler.js';
import { handleCodexProtocolEvent } from './codex-handler.js';
import { nextBlockId, durationSecs } from './parsers.js';
import { cancelInFlightTools } from './cancellation.js';
import { rendersNothing } from './turn-blocks/block-visibility.js';

/**
 * Devbox lifecycle events that are setup-phase infrastructure noise when they
 * arrive before the first user message. Only `devbox.running` qualifies: it
 * duplicates the setup progress card's "Provisioning devbox" step, which is
 * built from `agent.setup` events.
 *
 * Every other lifecycle event stays visible, because the setup card knows
 * nothing about devbox state: `devbox.suspended` and `devbox.shutdown` are the
 * only record that the box left the running state, and a cancelled launch that
 * resolves no setup step would otherwise leave an all-green setup card with no
 * sign of the teardown. `devbox.failed` signals a real provisioning failure.
 */
const SETUP_PHASE_DEVBOX_NOISE = new Set(['devbox.running']);

interface SDKStreamResult {
  messages: ChatItem[];
  currentTurnBlocks: TurnBlock[];
  isAgentTurn: boolean;
  availableCommands: AvailableCommand[];
  /** Directory the session runs in, for resolving the relative paths rows show. */
  workingDir: string | null;
  /**
   * Model the stream reports as currently answering, which a mid-session
   * `/model` switch moves off the launch-time model. Null until a session
   * announces one (see {@link ./active-model.ts}).
   */
  activeModel: string | null;
}

/**
 * Preserve derived row/block identities across a prefix rebuild when their
 * interpreted value did not change. The fold still has to replay for
 * correctness (an inserted historical result can repair a later tool call),
 * but React should not reconcile and remount every settled turn merely
 * because the fold recreated equivalent objects.
 *
 * Generated block ids are deliberately ignored only for event-anchored
 * objects: `nextBlockId` is process-local and changes on replay, while the
 * source event + block type identifies the same durable row. Unanchored
 * synthetic objects keep ordinary id equality.
 */
function isPlainRecord(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object') return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function generatedIdsAreEquivalent(
  previous: Record<string, unknown>,
  next: Record<string, unknown>,
): boolean {
  const previousSource = previous.sourceEvent as AxonEvent | undefined;
  const nextSource = next.sourceEvent as AxonEvent | undefined;
  return (
    previous.type === next.type &&
    typeof previous.type === 'string' &&
    previousSource?.id !== undefined &&
    previousSource.id === nextSource?.id
  );
}

/** Equality pass with no speculative array/object allocation. */
function derivedValueEqual(previous: unknown, next: unknown): boolean {
  if (Object.is(previous, next)) return true;
  if (Array.isArray(previous) && Array.isArray(next)) {
    return (
      previous.length === next.length &&
      previous.every((value, index) => derivedValueEqual(value, next[index]))
    );
  }
  if (!isPlainRecord(previous) || !isPlainRecord(next)) return false;
  if (Object.getPrototypeOf(previous) !== Object.getPrototypeOf(next)) return false;
  // Derived transcript data is arrays + plain JSON-like objects. Do not
  // mistake two Maps/Sets/class instances (whose state is not in Object.keys)
  // for equal empty records and accidentally retain stale state.
  const previousKeys = Object.keys(previous);
  const nextKeys = Object.keys(next);
  if (previousKeys.length !== nextKeys.length || previousKeys.some((key) => !(key in next))) {
    return false;
  }
  const ignoreGeneratedId = generatedIdsAreEquivalent(previous, next);
  return nextKeys.every(
    (key) => (key === 'id' && ignoreGeneratedId) || derivedValueEqual(previous[key], next[key]),
  );
}

function shareDerivedValue(previous: unknown, next: unknown): unknown {
  if (derivedValueEqual(previous, next)) return previous;
  if (Array.isArray(previous) && Array.isArray(next) && previous.length === next.length) {
    return next.map((value, index) => shareDerivedValue(previous[index], value));
  }
  if (!isPlainRecord(previous) || !isPlainRecord(next)) return next;
  if (Object.getPrototypeOf(previous) !== Object.getPrototypeOf(next)) return next;
  const previousKeys = Object.keys(previous);
  const nextKeys = Object.keys(next);
  if (previousKeys.length !== nextKeys.length || previousKeys.some((key) => !(key in next))) {
    return next;
  }
  const ignoreGeneratedId = generatedIdsAreEquivalent(previous, next);
  return Object.fromEntries(
    nextKeys.map((key) => [
      key,
      key === 'id' && ignoreGeneratedId
        ? previous[key]
        : shareDerivedValue(previous[key], next[key]),
    ]),
  );
}

function shareDerivedMessages(previous: ChatItem[], next: ChatItem[]): ChatItem[] {
  if (previous.length === 0 || next.length === 0) return next;
  const byId = new Map(previous.map((message) => [message.id, message]));
  let changed = previous.length !== next.length;
  const shared = next.map((message, index) => {
    const old = byId.get(message.id);
    const result = old ? (shareDerivedValue(old, message) as ChatItem) : message;
    if (!Object.is(result, previous[index])) changed = true;
    return result;
  });
  return changed ? shared : previous;
}

/**
 * Everything one fold writes to: a block manager for the live buffer, a
 * message store, and the per-turn flags the walk carries between events.
 * The hook folds the live tail into its own target; a paged-history prepend
 * folds the older prefix into a detached one, with the same code, and then
 * splices the result ahead of the mounted model.
 */
interface FoldTarget {
  blocks: BlockManager;
  messagesRef: MutableRefObject<ChatItem[]>;
  messagesDirtyRef: MutableRefObject<boolean>;
  turnFlushedRef: MutableRefObject<boolean>;
  interruptedRef: MutableRefObject<boolean>;
  /**
   * A system `turn.started` / `turn.resumed` has arrived with no turn end
   * since. Unlike `turnFlushedRef`, which stays clear while pending background
   * tasks hold a finished turn's blocks open, this says whether the agent is
   * actually mid-turn.
   */
  turnLiveRef: MutableRefObject<boolean>;
  /**
   * Set once the first user message event is folded. Gates
   * {@link SETUP_PHASE_DEVBOX_NOISE} suppression — see its doc comment.
   */
  hasSeenUserMessageRef: MutableRefObject<boolean>;
  setIsAgentTurn: (value: boolean) => void;
  setAvailableCommands: (commands: AvailableCommand[]) => void;
  setSetupEvents: (updater: (prev: AxonEvent[]) => AxonEvent[]) => void;
  classifiedEventCache: WeakMap<AxonEvent, ReturnType<typeof classifyEvent>>;
}

function updateMessagesOn(target: FoldTarget, updater: (prev: ChatItem[]) => ChatItem[]): void {
  target.messagesRef.current = updater(target.messagesRef.current);
  target.messagesDirtyRef.current = true;
}

function appendMessageTo(target: FoldTarget, msg: ChatItem): void {
  // A resumed turn keeps its blocks live until its own terminal event. Merge
  // the finished continuation only then, so streamed tool arguments/results
  // can still find their blocks between event batches.
  if (
    target.turnFlushedRef.current &&
    msg.role === 'assistant' &&
    'blocks' in msg &&
    msg.blocks &&
    mergeIntoLastAssistantOn(target, msg.blocks, msg)
  )
    return;
  target.messagesRef.current.push(msg);
  target.messagesDirtyRef.current = true;
}

function updateFlushedTaskOn(target: FoldTarget, taskId: string, update: Partial<TaskBlock>): void {
  const updateMessages = (updater: (prev: ChatItem[]) => ChatItem[]) =>
    updateMessagesOn(target, updater);

  updateMessages((prev) =>
    prev.map((msg) => {
      if (msg.role !== 'assistant' || !('blocks' in msg) || !msg.blocks) return msg;

      let changed = false;
      const nextBlocks = msg.blocks.map((block) => {
        if (block.type !== 'task' || block.taskId !== taskId) return block;
        changed = true;
        return { ...block, ...update };
      });

      return changed ? { ...msg, blocks: nextBlocks } : msg;
    }),
  );
}

/**
 * Resolve a `control_request` block that has already been flushed into a
 * finalized `messages` entry. Mirrors {@link updateFlushedTask} /
 * {@link updateFlushedToolCallOn}: a user `query` arriving between an
 * `AskUserQuestion`'s `control_request` and its `control_response` flushes
 * the live buffer, so the live-buffer resolver in `handleControlResponse`
 * misses the block.
 *
 * Scans `messagesRef` synchronously, then updates and warns *outside* the
 * state updater so React 19 StrictMode (dev) doesn't double-log. No-ops
 * silently when no flushed match exists — Claude emits `control_response`
 * for many request subtypes (`initialize`, `set_model`, `interrupt`) that
 * never produce a block, so the absence of a match is the common case, not
 * an anomaly. A truly orphaned reconnect doesn't surface a
 * `control_response` at all, so there is no honest "orphan" signal to log
 * from this code path.
 */
function updateFlushedControlRequestOn(
  target: FoldTarget,
  requestId: string,
  updatedInput: Record<string, unknown> | null,
): void {
  const { messagesRef } = target;
  const updateMessages = (updater: (prev: ChatItem[]) => ChatItem[]) =>
    updateMessagesOn(target, updater);

  let toolUseId: string | null = null;
  let found = false;
  for (const msg of messagesRef.current) {
    if (msg.role !== 'assistant' || !('blocks' in msg) || !msg.blocks) continue;
    const match = msg.blocks.find((b) => b.type === 'control_request' && b.requestId === requestId);
    if (match && match.type === 'control_request') {
      found = true;
      toolUseId = match.toolUseId;
      break;
    }
  }
  if (!found) return;

  updateMessages((prev) =>
    prev.map((msg) => {
      if (msg.role !== 'assistant' || !('blocks' in msg) || !msg.blocks) return msg;
      let changed = false;
      const nextBlocks = msg.blocks.map((block) => {
        if (block.type !== 'control_request' || block.requestId !== requestId) return block;
        changed = true;
        const mergedInput = updatedInput
          ? { ...(block.toolInput ?? {}), ...updatedInput }
          : block.toolInput;
        return { ...block, responded: true, toolInput: mergedInput };
      });
      return changed ? { ...msg, blocks: nextBlocks } : msg;
    }),
  );

  console.warn(
    'resolved AskUserQuestion from flushed message; out-of-order control_response (likely a user message interleaved with a pending question)',
    { requestId, toolUseId },
  );
}

/**
 * Resolve a `codex_approval` block that was already flushed into a
 * finalized message when its JSON-RPC `response` frame arrives. Mirrors
 * {@link updateFlushedControlRequestOn} for the native Codex protocol.
 * No-ops when no flushed match exists — most `response` frames answer
 * requests that never produced a block.
 */
function updateFlushedCodexApprovalOn(
  target: FoldTarget,
  requestId: string,
  result: Record<string, unknown> | null,
): void {
  const { messagesRef } = target;
  const updateMessages = (updater: (prev: ChatItem[]) => ChatItem[]) =>
    updateMessagesOn(target, updater);

  const found = messagesRef.current.some(
    (msg) =>
      msg.role === 'assistant' &&
      'blocks' in msg &&
      msg.blocks?.some((b) => b.type === 'codex_approval' && b.requestId === requestId),
  );
  if (!found) return;

  updateMessages((prev) =>
    prev.map((msg) => {
      if (msg.role !== 'assistant' || !('blocks' in msg) || !msg.blocks) return msg;
      let changed = false;
      const nextBlocks = msg.blocks.map((block) => {
        if (block.type !== 'codex_approval' || block.requestId !== requestId) return block;
        changed = true;
        return { ...block, responded: true, ...(result ? { result } : {}) };
      });
      return changed ? { ...msg, blocks: nextBlocks } : msg;
    }),
  );
}

/**
 * Update a tool_call block in already-flushed messages when its item
 * resolves after the flush: a Claude background task changing state, or a
 * native-Codex terminal item frame arriving after its turn's buffer was
 * flushed (mid-turn steer, or a long-running command whose process exits
 * after the turn completed). `idOrTaskId` is matched against either
 * `toolCallId` (for task_started linking and Codex item ids) or
 * `extra.backgroundTaskId` (for completion).
 */
function updateFlushedToolCallOn(
  target: FoldTarget,

  idOrTaskId: string,
  update: Partial<Pick<ToolCallBlock, 'status' | 'duration' | 'extra' | 'content' | 'rawOutput'>>,
  eventTimestamp: number,
): void {
  const updateMessages = (updater: (prev: ChatItem[]) => ChatItem[]) =>
    updateMessagesOn(target, updater);

  updateMessages((prev) =>
    prev.map((msg) => {
      if (msg.role !== 'assistant' || !('blocks' in msg) || !msg.blocks) return msg;

      let changed = false;
      const nextBlocks = msg.blocks.map((block) => {
        if (block.type !== 'tool_call') return block;
        const matchById = block.toolCallId === idOrTaskId;
        const matchByTask = block.extra?.backgroundTaskId === idOrTaskId;
        if (!matchById && !matchByTask) return block;
        changed = true;
        return {
          ...block,
          ...update,
          extra: { ...block.extra, ...update.extra },
          ...(update.status === 'completed' || update.status === 'failed'
            ? { duration: durationSecs(block.startedAt, eventTimestamp) }
            : {}),
        };
      });

      return changed ? { ...msg, blocks: nextBlocks } : msg;
    }),
  );
}

/**
 * Record that the user interrupted the current turn and reconcile the UI to
 * a "Cancelled" state. Two orderings occur relative to the turn-end event:
 *
 * - Interrupt arrives *before* the flush (live blocks still pending): mark
 *   in-flight tool calls cancelled and set the flag so the upcoming turn-end
 *   flush (system `turn.*` / Claude `result`) renders the turn as cancelled.
 * - Interrupt arrives *after* the flush (`turnFlushedRef` set): the turn
 *   already flushed under the agent's native stop reason, so retroactively
 *   relabel the just-finished assistant message as cancelled.
 */
function markTurnInterruptedOn(target: FoldTarget, eventTimestamp: number): void {
  const { blocks, turnFlushedRef, interruptedRef } = target;
  const updateMessages = (updater: (prev: ChatItem[]) => ChatItem[]) =>
    updateMessagesOn(target, updater);

  interruptedRef.current = true;
  blocks.updateBlocks((prev) => cancelInFlightTools(prev, eventTimestamp));
  if (turnFlushedRef.current) {
    updateMessages((prev) => relabelLastAssistantCancelled(prev, eventTimestamp));
  }
}

/**
 * ACP agents can deliver content events after turn.completed, even across
 * batches. If the turn was already flushed but new blocks accumulated from
 * late-arriving events, merge them into the last assistant message.
 */
function mergeOrphanedBlocksOn(target: FoldTarget): void {
  const { blocks, turnFlushedRef, turnLiveRef, setIsAgentTurn } = target;

  if (!turnFlushedRef.current || turnLiveRef.current) return;
  const orphaned = blocks.blocksRef.current;
  if (orphaned.length === 0) return;

  // Clear blocks so they don't render as a separate live turn. Not a full
  // reset: the session is still the same one, so what it announced (working
  // directory, active model) has to survive.
  blocks.clearLiveBlocks();

  setIsAgentTurn(false);

  mergeIntoLastAssistantOn(target, orphaned);
}

/** Merge a finished continuation or late blocks without replacing the message identity. */
function mergeIntoLastAssistantOn(
  target: FoldTarget,
  orphaned: TurnBlock[],
  continuation?: ChatMessage,
): boolean {
  let merged = false;
  // Consolidate consecutive text/thinking blocks into the same bubble.
  updateMessagesOn(target, (prev) => {
    for (let i = prev.length - 1; i >= 0; i--) {
      const msg = prev[i];
      if (msg.role === 'assistant' && 'blocks' in msg && msg.blocks) {
        const updated = [...prev];
        const mergedBlocks = [...msg.blocks];
        for (const orphan of orphaned) {
          const last = mergedBlocks[mergedBlocks.length - 1];
          if (last?.type === 'text' && orphan.type === 'text') {
            mergedBlocks[mergedBlocks.length - 1] = { ...last, text: last.text + orphan.text };
          } else if (last?.type === 'thinking' && orphan.type === 'thinking') {
            mergedBlocks[mergedBlocks.length - 1] = { ...last, text: last.text + orphan.text };
          } else if (last?.type === 'agent_log' && orphan.type === 'agent_log') {
            mergedBlocks[mergedBlocks.length - 1] = {
              ...last,
              lines: [...last.lines, ...orphan.lines],
              timestamp: orphan.timestamp,
            };
          } else {
            mergedBlocks.push(orphan);
          }
        }
        updated[i] = {
          ...msg,
          ...continuation,
          id: msg.id,
          blocks: mergedBlocks,
          cost: continuation?.cost ?? msg.cost,
          numTurns: continuation?.numTurns ?? msg.numTurns,
          durationMs:
            continuation?.durationMs === undefined
              ? msg.durationMs
              : (msg.durationMs ?? 0) + continuation.durationMs,
        };
        merged = true;
        return updated;
      }
    }
    return prev;
  });
  return merged;
}

/**
 * Add a broker log line to the turn already on screen, if there is one.
 *
 * The test is literally "the live buffer holds a block this line would land
 * after", not "a turn is in flight" — deliberately, because only the former
 * guarantees the line has somewhere in-order to go. A line logged before the
 * turn's first block has no such place: buffering it would open a live
 * assistant card containing nothing but a console, and one logged after a turn
 * flushed would open that card outside any turn at all. Both stay on the
 * timeline as their own row, which is the shape boot output already takes.
 *
 * A buffer holding only a `system_init` block doesn't count:
 * {@link BlockManager.flushToMessage} holds that block back so it attaches to
 * the turn that follows, and session boot logs land in exactly that window.
 *
 * Adjacent lines accumulate into one block, so a run renders as a single
 * console card just like the timeline row does.
 */
function appendAgentLogBlock(text: string, blocks: BlockManager, sourceEvent: AxonEvent): boolean {
  const live = blocks.blocksRef.current;
  if (!live.some((block) => block.type !== 'system_init' && !rendersNothing(block))) return false;

  const last = blocks.lastBlock();
  const line = { id: `log-${sourceEvent.id}`, text, sourceEvent };
  if (last?.type === 'agent_log') {
    blocks.updateBlocks((current) => {
      const currentLast = current[current.length - 1];
      if (currentLast?.id !== last.id || currentLast.type !== 'agent_log') return current;
      return [
        ...current.slice(0, -1),
        {
          ...currentLast,
          // `sourceEvent` stays the run's first event so the card's identity
          // is stable as lines arrive; each line carries its own.
          lines: [...currentLast.lines, line],
          timestamp: sourceEvent.timestamp,
        },
      ];
    });
    return true;
  }

  blocks.pushBlock({
    type: 'agent_log',
    id: nextBlockId('log'),
    lines: [line],
    timestamp: sourceEvent.timestamp,
    sourceEvent,
  });
  return true;
}

/** Return a broker stdout/stderr line that belongs in the log console. */
function brokerLogLine(event: AxonEvent): string | null {
  const payload = (
    typeof event.payload === 'object' && event.payload !== null ? event.payload : {}
  ) as Record<string, unknown>;
  const isLogLine =
    (event.type === 'agent.error' && payload.errorType === 'stderr') ||
    (event.type === 'agent.log' &&
      (payload.log_type === 'stderr' || payload.log_type === 'stdout'));
  if (!isLogLine) return null;
  return typeof payload.message === 'string' ? payload.message : 'stderr';
}

/**
 * Fold a run of events into the target, in stream order. The one place that
 * knows how an event becomes rows and blocks; the live tail and a history
 * prefix both go through here.
 */
function foldEvents(target: FoldTarget, newEvents: readonly AxonEvent[]): void {
  const {
    blocks,
    turnFlushedRef,
    interruptedRef,
    turnLiveRef,
    hasSeenUserMessageRef,
    setIsAgentTurn,
    setAvailableCommands,
    setSetupEvents,
    classifiedEventCache,
  } = target;
  const appendMessage = (msg: ChatItem) => appendMessageTo(target, msg);
  const markTurnInterrupted = (eventTimestamp: number) =>
    markTurnInterruptedOn(target, eventTimestamp);
  const updateFlushedTask = (taskId: string, update: Partial<TaskBlock>) =>
    updateFlushedTaskOn(target, taskId, update);
  const updateFlushedToolCall = (
    idOrTaskId: string,
    update: Partial<Pick<ToolCallBlock, 'status' | 'duration' | 'extra' | 'content' | 'rawOutput'>>,
    eventTimestamp: number,
  ) => updateFlushedToolCallOn(target, idOrTaskId, update, eventTimestamp);
  const updateFlushedControlRequest = (
    requestId: string,
    updatedInput: Record<string, unknown> | null,
  ) => updateFlushedControlRequestOn(target, requestId, updatedInput);
  const updateFlushedCodexApproval = (requestId: string, result: Record<string, unknown> | null) =>
    updateFlushedCodexApprovalOn(target, requestId, result);

  for (const event of newEvents) {
    // ACP cancel request. Handled before classification so the in-flight
    // tool is marked cancelled *before* the agent's own terminal
    // tool_call_update (e.g. OpenCode's post-abort `completed`) lands —
    // `cancelled` is sticky, so that later update can't revert it. The
    // protocol-agnostic `agent.interrupted` (below) also calls this; both
    // are emitted on a user interrupt.
    if (event.type === 'session/cancel') {
      markTurnInterrupted(event.timestamp);
      continue;
    }

    // Check for user messages first (across all protocols)
    if (isUserMessageEvent(event)) {
      hasSeenUserMessageRef.current = true;
      // Flush any pending assistant blocks before the user message
      turnFlushedRef.current = false;
      interruptedRef.current = false;
      blocks.pendingResultRef.current = null;
      const flushed = blocks.flushToMessage(undefined, event.timestamp);
      if (flushed) appendMessage(flushed);

      const payload = (event.payload ?? {}) as Record<string, unknown>;
      const text = extractUserText(payload);
      const imageAttachments = extractUserImageAttachments(payload);
      const fileAttachments = extractFileEnvelopeAttachments(payload);
      const attachments = [...imageAttachments, ...fileAttachments];
      if (text || attachments.length > 0) {
        appendMessage({
          id: `user-${event.id}`,
          role: 'user',
          content: text,
          sourceEvent: event,
          ...(attachments.length > 0 ? { attachments } : {}),
        });
      }
      // A steer lands inside a still-running turn; the agent keeps going.
      if ((event.type as string) !== 'turn/steer') setIsAgentTurn(false);
      continue;
    }

    let classified = classifiedEventCache.get(event);
    if (!classified) {
      classified = classifyEvent(event);
      classifiedEventCache.set(event, classified);
    }

    switch (classified.kind) {
      case 'system':
        if (SETUP_PHASE_DEVBOX_NOISE.has(event.type) && !hasSeenUserMessageRef.current) break;
        handleSystemTimelineEvent(
          classified.data,
          blocks,
          appendMessage,
          setIsAgentTurn,
          event,
          turnFlushedRef,
          interruptedRef,
          turnLiveRef,
          event.timestamp,
        );
        break;

      case 'claude_protocol': {
        // Snapshot the live buffer before dispatch so we can detect the
        // post-flush race where an interleaved user `query` moved the
        // pending `control_request` block out of the live buffer. The
        // primary live-buffer path is unchanged — the fallback only runs
        // when no live block exists for this `request_id`.
        const controlResponseParsed =
          classified.data.type === 'control_response'
            ? parseControlResponse(classified.data)
            : null;
        const liveControlRequestPresent =
          controlResponseParsed && controlResponseParsed.requestId
            ? blocks.blocksRef.current.some(
                (b) =>
                  b.type === 'control_request' && b.requestId === controlResponseParsed.requestId,
              )
            : false;

        handleClaudeProtocolEvent(
          classified.data,
          blocks,
          appendMessage,
          setIsAgentTurn,
          event.timestamp,
          event.origin,
          event,
          interruptedRef.current,
        );

        if (
          controlResponseParsed &&
          controlResponseParsed.requestId &&
          !liveControlRequestPresent
        ) {
          updateFlushedControlRequest(
            controlResponseParsed.requestId,
            controlResponseParsed.updatedInput,
          );
        }

        updateFlushedClaudeTask(
          classified.data,
          updateFlushedTask,
          updateFlushedToolCall,
          event.timestamp,
        );
        // Extract slash commands from Claude system init
        if (
          (classified.data.type as string) === 'system' &&
          (classified.data.subtype as string) === 'init'
        ) {
          const cmds = classified.data.slash_commands as string[] | undefined;
          if (Array.isArray(cmds) && cmds.length > 0) {
            setAvailableCommands(cmds.map((name) => ({ name })));
          }
        }
        break;
      }

      case 'acp_protocol':
        handleAcpProtocolEvent(
          classified.data,
          blocks,
          appendMessage,
          setIsAgentTurn,
          event,
          setAvailableCommands,
        );
        break;

      case 'codex_protocol':
        // Native slash commands do not start a model turn. Close any
        // lingering assistant buffer first so their result is a standalone
        // system note instead of being attached to the previous response.
        if (classified.data.type === 'codex.command.result') {
          const flushed = blocks.flushToMessage(undefined, event.timestamp);
          if (flushed) appendMessage(flushed);
          setIsAgentTurn(false);
        }
        // A `response` frame resolving an approval whose block was already
        // flushed into a finished message (e.g. by a mid-turn user message)
        // is invisible to the live-buffer resolver in the handler.
        if (classified.data.type === 'response' && classified.data.id != null) {
          const requestId = String(classified.data.id);
          const liveApprovalPresent = blocks.blocksRef.current.some(
            (b) => b.type === 'codex_approval' && b.requestId === requestId,
          );
          if (!liveApprovalPresent) {
            const result = classified.data.result;
            updateFlushedCodexApproval(
              requestId,
              result != null && typeof result === 'object' && !Array.isArray(result)
                ? (result as Record<string, unknown>)
                : null,
            );
          }
        }
        handleCodexProtocolEvent(
          classified.data,
          blocks,
          appendMessage,
          setIsAgentTurn,
          event,
          setAvailableCommands,
          updateFlushedTask,
          updateFlushedToolCall,
        );
        break;

      case 'unknown': {
        // agent.setup events → collect for SetupProgressItem
        if (event.type === 'agent.setup') {
          setSetupEvents((prev) => [...prev, event]);
          break;
        }

        const configItem = buildAgentConfigItem(event);
        if (configItem) {
          configItem.sourceEvent = event;
          appendMessage(configItem);
          break;
        }

        const lifecycleItem = buildAgentLifecycleItem(event);
        if (lifecycleItem) {
          const logLine = brokerLogLine(event);
          // agent.log events with any other log_type aren't renderable — skip them
          // rather than appending a bare "Log" lifecycle banner.
          if (event.type === 'agent.log' && logLine === null) break;
          if (logLine !== null) {
            lifecycleItem.eventType = 'agent.stderr';
            lifecycleItem.label = logLine;
            // A line logged while the turn is mid-flight belongs inside it.
            // Falling through to the timeline path below would flush the live
            // buffer to close a message the agent has not finished, splitting
            // one response into two cards around the log — the common case for
            // harnesses that log as they work, like Codex's `tracing` output.
            if (appendAgentLogBlock(lifecycleItem.label, blocks, event)) break;
          }
          lifecycleItem.sourceEvent = event;
          // `agent.interrupted` (reflex) is the protocol-agnostic user-cancel
          // signal. Record it and reconcile to a cancelled state; don't flush
          // or end the turn here — the turn-end event (system `turn.*` /
          // Claude `result`) owns the flush and will render it cancelled.
          if (event.type === 'agent.interrupted') {
            markTurnInterrupted(event.timestamp);
          } else {
            const flushed = blocks.flushToMessage(undefined, event.timestamp);
            if (flushed) appendMessage(flushed);
          }
          appendMessage(lifecycleItem);
          if (logLine === null && event.type !== 'agent.interrupted') setIsAgentTurn(false);
        }
        break;
      }
    }
  }

  mergeOrphanedBlocksOn(target);
}

/** What folding a history prefix in isolation produced. */
interface PrefixFold {
  messages: ChatItem[];
  /** The detached manager the prefix folded through, for its end state. */
  blocks: BlockManager;
  /**
   * Blocks still buffered when the prefix ran out — only ever `system_init`
   * blocks, which the flush deliberately holds back so they attach to the
   * next real assistant turn (the one that opens the loaded window).
   */
  leftoverBlocks: TurnBlock[];
  workingDir: string | null;
  activeModel: string | null;
  switchSequence: number | null;
  availableCommands: AvailableCommand[] | null;
  setupEvents: AxonEvent[];
}

/**
 * Fold older history on its own, without touching the mounted model. A
 * turn is self-contained — it opens at a user prompt and everything it
 * needs arrives before the next one — so a prefix made of whole turns
 * folds to exactly the rows a full replay would put ahead of the window.
 */
function foldPrefix(
  prefix: readonly AxonEvent[],
  classifiedEventCache: FoldTarget['classifiedEventCache'],
): PrefixFold {
  const blocks = createBlockManager();
  let availableCommands: AvailableCommand[] | null = null;
  let setupEvents: AxonEvent[] = [];
  const target: FoldTarget = {
    blocks,
    messagesRef: { current: [] },
    messagesDirtyRef: { current: false },
    turnFlushedRef: { current: false },
    interruptedRef: { current: false },
    turnLiveRef: { current: false },
    hasSeenUserMessageRef: { current: false },
    setIsAgentTurn: () => {},
    setAvailableCommands: (commands) => {
      availableCommands = commands;
    },
    setSetupEvents: (updater) => {
      setupEvents = updater(setupEvents);
    },
    classifiedEventCache,
  };
  foldEvents(target, prefix);
  // A prefix that ends mid-turn (the window opens after an interrupted or
  // still-running turn) flushes like a prompt would have.
  const last = prefix[prefix.length - 1];
  const flushed = last ? blocks.flushToMessage(undefined, last.timestamp) : null;
  if (flushed) appendMessageTo(target, flushed);
  const { workingDir, activeModel, switchSequence } = blocks.snapshot();
  return {
    messages: target.messagesRef.current,
    blocks,
    leftoverBlocks: blocks.blocksRef.current,
    workingDir,
    activeModel,
    switchSequence,
    availableCommands,
    setupEvents,
  };
}

/**
 * Whether the state a prefix fold ends with would have changed how the
 * window folded. The block manager carries state forward across turns —
 * accumulated plan entries, Codex token baselines and flushed item text,
 * subagent threads, session-init dedupe, a pending background-task result —
 * and a full replay hands all of it to the turns that follow. A window
 * folded before the prefix arrived never had it; when the window has rows
 * that consume it, only a replay makes them right.
 */
function prefixStateReachesWindow(
  prefix: BlockManager,
  windowMessages: readonly ChatItem[],
  liveBlocks: readonly TurnBlock[],
): boolean {
  if (
    prefix.pendingResultRef.current !== null ||
    prefix.streamingTodoInputRef.current !== null ||
    prefix.codexTokenUsageRef.current !== null ||
    prefix.codexFlushedItemTextRef.current.size > 0 ||
    prefix.codexSubagentsRef.current.size > 0 ||
    prefix.codexSubagentTerminalsRef.current > 0 ||
    prefix.codexSessionInitRef.current.pending !== null ||
    prefix.codexSessionInitRef.current.requestMethods.size > 0 ||
    prefix.codexSessionInitRef.current.emittedSessionIds.size > 0 ||
    prefix.acpEmittedSessionIds.current.size > 0
  ) {
    return true;
  }
  // Plan entries only ever feed plan blocks: a window without one is
  // unaffected, however long the prefix's list is.
  if (prefix.planEntriesRef.current.length === 0) return false;
  if (liveBlocks.some((b) => b.type === 'plan')) return true;
  return windowMessages.some(
    (msg) =>
      msg.role === 'assistant' && 'blocks' in msg && msg.blocks?.some((b) => b.type === 'plan'),
  );
}

/**
 * Stamp a row folded before the session announced itself with what the
 * announcement turned out to be: the model on the row, the working
 * directory on each block still carrying none. Rows already stamped are
 * returned as they are.
 */
function restampSessionFacts(
  msg: ChatItem,
  learned: { workingDir: string | null; activeModel: string | null },
  known: { workingDir: string | null; activeModel: string | null },
): ChatItem {
  if (msg.role !== 'assistant' || !('blocks' in msg)) return msg;
  let next = msg;
  if (learned.activeModel && !known.activeModel && !msg.model) {
    next = { ...next, model: learned.activeModel };
  }
  if (learned.workingDir && !known.workingDir && msg.blocks?.some((b) => !b.workingDir)) {
    next = {
      ...next,
      blocks: msg.blocks.map((b) => (b.workingDir ? b : { ...b, workingDir: learned.workingDir })),
    };
  }
  return next;
}

/**
 * Where a loaded window's old first event now sits, if the array grew only
 * by whole turns ahead of it: the old first event is found again at `k > 0`,
 * the old last event is exactly `oldLength` behind it, and the old first
 * event opens a turn, so nothing folded so far depended on the new prefix.
 * Anything else (a replaced array, a middle backfill, a window that opened
 * mid-turn) is not a prepend and takes the full replay.
 */
function pureTurnPrependLength(
  events: readonly AxonEvent[],
  oldFirstId: string | null,
  oldLastId: string | null,
  oldLength: number,
): number {
  if (oldFirstId === null || oldLastId === null || oldLength === 0) return 0;
  if (events[0]?.id === oldFirstId) return 0;
  const k = events.findIndex((event) => event.id === oldFirstId);
  if (k <= 0) return 0;
  if (events[k + oldLength - 1]?.id !== oldLastId) return 0;
  const opening = events[k];
  if (!opening || !opensTurn(opening)) return 0;
  return k;
}

/**
 * A prompt that starts a turn. A Codex `turn/steer` is a user message too,
 * but it lands inside a running turn: a window opening on one was folded
 * without the half of the turn before it, so nothing ahead of it can be
 * folded in isolation.
 */
function opensTurn(event: AxonEvent): boolean {
  return isUserMessageEvent(event) && (event.type as string) !== 'turn/steer';
}

/**
 * Hook that converts raw AxonEvent[] into the SDK chat model using the
 * timeline event classification pattern from the combined-app example.
 *
 * Accepts a `protocol` parameter (derived from agent.type) to dispatch
 * to the correct protocol handler. Falls back to auto-detection when
 * protocol is null.
 */
export function useSDKStream(
  events: AxonEvent[],
  protocol?: 'claude-code' | 'opencode' | 'codex' | null,
): SDKStreamResult {
  const blocks = useBlockManager();
  const [messages, setMessages] = useState<ChatItem[]>([]);
  /**
   * Write-through mirror of `messages` so handlers running inside the event
   * loop can read the latest finalized messages synchronously — e.g. the
   * AskUserQuestion fallback in {@link updateFlushedControlRequest} needs to
   * scan flushed blocks for a `requestId` match in the same useEffect run
   * that just appended a user message. React's batched setState wouldn't
   * surface that update synchronously, so every messages mutation goes
   * through {@link updateMessages} / {@link appendMessage} which update the
   * ref immediately. `setMessages` is deferred to {@link commitMessages} at
   * the end of the fold so a giant history load does not copy the growing
   * array on every append (O(n²)).
   */
  const messagesRef = useRef<ChatItem[]>([]);
  // Last array handed to React. It survives a prefix reset so the rebuilt
  // fold can structurally share settled turns with the mounted transcript.
  const committedMessagesRef = useRef<ChatItem[]>([]);
  const messagesDirtyRef = useRef(false);
  const commitMessages = useCallback(() => {
    if (!messagesDirtyRef.current) return;
    messagesDirtyRef.current = false;
    const shared = shareDerivedMessages(committedMessagesRef.current, messagesRef.current);
    // Keep distinct working and committed arrays even when structural sharing
    // returned the previous committed array wholesale: later in-place pushes
    // must never mutate the array React already holds as state.
    messagesRef.current = shared.slice();
    const committed = shared.slice();
    committedMessagesRef.current = committed;
    setMessages(committed);
  }, []);
  const [isAgentTurn, setIsAgentTurn] = useState(false);
  const lastProcessedRef = useRef(0);
  /**
   * Id of the last event folded into the model. Together with
   * `lastProcessedRef` it anchors the incremental walk below to a specific
   * prefix of `events`, so a reconciliation that rewrites history in place
   * (the reconnect / tab-refocus REST backfill merges missed events into the
   * middle of the array) is detected instead of being mistaken for new tail.
   */
  const lastProcessedIdRef = useRef<string | null>(null);
  /**
   * Id of the first event folded into the model. A paged-history prepend
   * moves it to a later index without touching anything folded so far,
   * which is how the walk below tells a prepend from a rewrite.
   */
  const firstProcessedIdRef = useRef<string | null>(null);
  const prevEventsLenRef = useRef(0);
  /**
   * Tracks whether the turn has been flushed (turn.completed/turn.failed
   * processed) so that late-arriving ACP content blocks can be merged into
   * the last assistant message instead of creating orphaned live bubbles.
   */
  const turnFlushedRef = useRef(false);
  /**
   * Tracks whether the user interrupted the current turn. Set by the
   * protocol-agnostic `agent.interrupted` (reflex) signal — and the ACP
   * `session/cancel` request, so an in-flight tool is marked cancelled before
   * the agent's own terminal update lands. Reset on the next `turn.started`
   * or user message. The turn-end flush reads this to render "Cancelled"
   * instead of the agent's native stop reason (OpenCode `end_turn`, Claude
   * `error_during_execution`).
   */
  const interruptedRef = useRef(false);
  /** See `FoldTarget.turnLiveRef`. */
  const turnLiveRef = useRef(false);
  /**
   * Set once the first user message event is processed. A `devbox.running`
   * that arrives before this is setup-phase noise already covered by the
   * setup progress card — especially confusing for cancelled launches where
   * the user's prompt never reaches the stream. See
   * {@link SETUP_PHASE_DEVBOX_NOISE} for why nothing else is suppressed.
   */
  const hasSeenUserMessageRef = useRef(false);
  // Durable Axon event objects survive paged prefix merges by identity. Their
  // classification is pure, so retain it across the correctness replay and
  // avoid reparsing/reallocating every already-loaded payload on each turn
  // prepend. Weak keys release with the query cache.
  const classifiedEventCacheRef = useRef<WeakMap<
    AxonEvent,
    ReturnType<typeof classifyEvent>
  > | null>(null);
  if (classifiedEventCacheRef.current === null) classifiedEventCacheRef.current = new WeakMap();

  const [setupEvents, setSetupEvents] = useState<AxonEvent[]>([]);
  const [setupComplete, setSetupComplete] = useState(false);
  const [availableCommands, setAvailableCommands] = useState<AvailableCommand[]>([]);

  /**
   * Drop the derived model so the next pass rebuilds it from event zero.
   * Every ref the incremental walk carries across renders has to be cleared
   * here, or the rebuild replays events against stale turn state.
   */
  const resetStream = useCallback(
    (preserveCommitted = false) => {
      blocks.reset();
      messagesRef.current = [];
      messagesDirtyRef.current = false;
      setMessages([]);
      setIsAgentTurn(false);
      setSetupEvents([]);
      setSetupComplete(false);
      setAvailableCommands([]);
      lastProcessedRef.current = 0;
      lastProcessedIdRef.current = null;
      firstProcessedIdRef.current = null;
      turnFlushedRef.current = false;
      interruptedRef.current = false;
      turnLiveRef.current = false;
      hasSeenUserMessageRef.current = false;
      if (!preserveCommitted) committedMessagesRef.current = [];
    },
    [blocks],
  );

  // Reset when the event array is replaced (new agent or full refresh)
  useEffect(() => {
    if (events.length < prevEventsLenRef.current) {
      resetStream();
    }
    prevEventsLenRef.current = events.length;
  }, [events.length, resetStream]);

  useEffect(() => {
    const classifiedEventCache = classifiedEventCacheRef.current!;
    const target: FoldTarget = {
      blocks,
      messagesRef,
      messagesDirtyRef,
      turnFlushedRef,
      interruptedRef,
      turnLiveRef,
      hasSeenUserMessageRef,
      setIsAgentTurn,
      setAvailableCommands,
      setSetupEvents,
      classifiedEventCache,
    };

    // Older whole turns landed ahead of the loaded window (a paged-history
    // prepend): fold just those, in isolation, and splice the rows ahead of
    // the mounted model. Nothing already folded is touched, so the cost is
    // the new turns, not the whole window, and settled rows keep identity.
    const prepended = pureTurnPrependLength(
      events,
      firstProcessedIdRef.current,
      lastProcessedIdRef.current,
      lastProcessedRef.current,
    );
    const prefix =
      prepended > 0 ? foldPrefix(events.slice(0, prepended), classifiedEventCache) : null;
    // State the prefix would have carried into the window's turns cannot be
    // spliced in: those rows take the full replay below (their anchor is
    // still present, so settled rows keep identity where their value holds).
    if (
      prefix &&
      !prefixStateReachesWindow(prefix.blocks, messagesRef.current, blocks.blocksRef.current)
    ) {
      let existing = messagesRef.current;
      // The held-back session-init blocks attach to the turn that opens the
      // window, exactly where a full replay would have put them: the first
      // finalized assistant row, or the live buffer when that turn is still
      // running.
      if (prefix.leftoverBlocks.length > 0) {
        const index = existing.findIndex(
          (msg) => msg.role === 'assistant' && 'blocks' in msg && Array.isArray(msg.blocks),
        );
        const msg = index >= 0 ? existing[index] : undefined;
        if (msg && msg.role === 'assistant' && 'blocks' in msg && msg.blocks) {
          existing = existing.slice();
          existing[index] = { ...msg, blocks: [...prefix.leftoverBlocks, ...msg.blocks] };
        } else {
          blocks.updateBlocks((prev) => [...prefix.leftoverBlocks, ...prev]);
        }
      }
      // Session facts announced in the prefix are older than anything the
      // window already knows: they only fill in what is still unknown. A
      // fact the window never had was also missing from every row it
      // stamped, so those rows are restamped the way a full replay would
      // have stamped them — once, when the start of the thread arrives.
      const known = blocks.snapshot();
      blocks.seedSessionFacts({
        workingDir: prefix.workingDir,
        activeModel: prefix.activeModel,
        switchSequence: prefix.switchSequence,
      });
      const learned = blocks.snapshot();
      if (learned.workingDir !== known.workingDir || learned.activeModel !== known.activeModel) {
        existing = existing.map((msg) => restampSessionFacts(msg, learned, known));
      }
      messagesRef.current = [...prefix.messages, ...existing];
      messagesDirtyRef.current = true;
      if (prefix.availableCommands) {
        setAvailableCommands((prev) => (prev.length === 0 ? prefix.availableCommands! : prev));
      }
      if (prefix.setupEvents.length > 0) {
        setSetupEvents((prev) => [...prefix.setupEvents, ...prev]);
      }
      lastProcessedRef.current += prepended;
      firstProcessedIdRef.current = events[0]?.id ?? null;
    }

    // History rewritten under us: the event we last processed is no longer at
    // the end of the processed prefix, so a reconciliation inserted events
    // before it. Replaying only the tail would drop those (and, for a tool
    // call whose result was backfilled after its turn had already been
    // flushed, leave the call spinning as "Running" forever), so rebuild the
    // whole model from the merged array.
    if (
      lastProcessedIdRef.current !== null &&
      events[lastProcessedRef.current - 1]?.id !== lastProcessedIdRef.current
    ) {
      // Retain the mounted model only when its anchor still exists: that is a
      // prefix/middle reconciliation. A new agent or replacement snapshot has
      // no old anchor and must not share coincidentally-equal ids across runs.
      const oldAnchorStillPresent = events.some((event) => event.id === lastProcessedIdRef.current);
      resetStream(oldAnchorStillPresent);
    }

    if (events.length <= lastProcessedRef.current) {
      commitMessages();
      return;
    }

    const newEvents = events.slice(lastProcessedRef.current);
    if (lastProcessedRef.current === 0) firstProcessedIdRef.current = events[0]?.id ?? null;
    lastProcessedRef.current = events.length;
    lastProcessedIdRef.current = events[events.length - 1]?.id ?? null;

    foldEvents(target, newEvents);
    commitMessages();
  }, [events, protocol, blocks, commitMessages, resetStream]);

  // Mark setup complete when the first protocol event arrives after setup events.
  // Setup is NOT complete just because the devbox is up — the broker still has to
  // connect to Tailscale, install code-server, and launch the agent process. Only
  // actual agent/protocol events (assistant, query, turn.*, agent_started, etc.)
  // indicate the agent is running.
  useEffect(() => {
    if (setupComplete || setupEvents.length === 0) return;
    const hasProtocolEvent = events.some((e) => {
      if (e.type === 'agent.setup' || e.type.startsWith('broker.')) return false;
      // Devbox lifecycle events fire during setup (e.g. devbox.running arrives
      // right after the devbox is provisioned, before the remaining setup steps).
      // Treat them as system lifecycle, not as protocol activity.
      if (e.type.startsWith('devbox.')) return false;
      // Broker stderr lines (agent.error with errorType "stderr") are not protocol events
      if (e.type === 'agent.error') {
        const p = e.payload as Record<string, unknown> | null;
        if (p?.errorType === 'stderr') return false;
      }
      return true;
    });
    if (hasProtocolEvent) setSetupComplete(true);
  }, [events, setupEvents.length, setupComplete]);

  // Build final messages with setup progress injected at the top
  const messagesWithSetup = useMemo<ChatItem[]>(() => {
    if (setupEvents.length === 0) return messages;

    const setupItem: SetupProgressItem = {
      id: 'setup-progress',
      role: 'system',
      itemType: 'setup_progress',
      setupEvents,
      isComplete: setupComplete,
    };

    return [setupItem, ...messages];
  }, [messages, setupEvents, setupComplete]);

  return {
    messages: messagesWithSetup,
    currentTurnBlocks: blocks.currentTurnBlocks,
    isAgentTurn,
    availableCommands,
    workingDir: blocks.workingDir,
    activeModel: blocks.activeModel,
  };
}

function isTaskStatus(status: unknown): status is TaskBlock['status'] {
  return (
    status === 'started' ||
    status === 'in_progress' ||
    status === 'completed' ||
    status === 'failed' ||
    status === 'stopped'
  );
}

function updateFlushedClaudeTask(
  data: Record<string, unknown>,
  updateTask: (taskId: string, update: Partial<TaskBlock>) => void,
  updateFlushedToolCall: (
    taskId: string,
    update: Partial<Pick<ToolCallBlock, 'status' | 'duration' | 'extra'>>,
    eventTimestamp: number,
  ) => void,
  eventTimestamp: number,
): void {
  if (data.type !== 'system') return;

  const subtype = data.subtype;

  // Handle task_started linking to tool_call blocks in flushed messages
  if (subtype === 'task_started') {
    const toolUseId = typeof data.tool_use_id === 'string' ? data.tool_use_id : null;
    const taskId = typeof data.task_id === 'string' ? data.task_id : null;
    if (toolUseId && taskId) {
      updateFlushedToolCall(
        toolUseId,
        {
          status: 'in_progress',
          extra: {
            backgroundTaskId: taskId,
            backgroundDescription: data.description,
            backgroundTaskType: typeof data.task_type === 'string' ? data.task_type : undefined,
          },
        },
        eventTimestamp,
      );
    }
    return;
  }

  if (
    subtype !== 'task_updated' &&
    subtype !== 'task_notification' &&
    subtype !== 'task_completed'
  ) {
    return;
  }

  const taskId = typeof data.task_id === 'string' ? data.task_id : null;
  if (!taskId) return;

  const patch = data.patch as Record<string, unknown> | undefined;
  const status =
    patch?.status ?? data.status ?? (subtype === 'task_completed' ? 'completed' : undefined);
  const summary = typeof data.summary === 'string' ? data.summary : undefined;

  // Try updating a linked tool_call block first
  const toolCallStatus =
    status === 'completed'
      ? 'completed'
      : status === 'failed' || status === 'stopped'
        ? 'failed'
        : undefined;
  if (toolCallStatus) {
    updateFlushedToolCall(
      taskId,
      {
        status: toolCallStatus as ToolCallStatus,
        extra: { backgroundSummary: summary },
      },
      eventTimestamp,
    );
  }

  // Also update any standalone task blocks
  const update: Partial<TaskBlock> = {};
  if (isTaskStatus(status)) update.status = status;
  if (summary) update.summary = summary;
  if (Object.keys(update).length === 0) return;

  updateTask(taskId, update);
}

/**
 * Retroactively relabel the most recent assistant message as cancelled. Used
 * when the user interrupt (`agent.interrupted`) arrives *after* the turn-end
 * event already flushed the message under the agent's native stop reason
 * (OpenCode `end_turn`, Claude `error_during_execution`). Clears the error
 * flag and re-finalizes any non-terminal tool calls so the bubble matches a
 * turn that was cancelled before the flush.
 */
function relabelLastAssistantCancelled(messages: ChatItem[], eventTimestamp: number): ChatItem[] {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.role !== 'assistant' || !('blocks' in msg)) continue;
    const updated = [...messages];
    updated[i] = {
      ...msg,
      stopReason: 'cancelled',
      isError: false,
      ...(msg.blocks ? { blocks: cancelInFlightTools(msg.blocks, eventTimestamp) } : {}),
    };
    return updated;
  }
  return messages;
}

/**
 * Handle system timeline events (turn boundaries, broker errors).
 */
function handleSystemTimelineEvent(
  data: Record<string, unknown>,
  blocks: BlockManager,
  appendMessage: (msg: ChatItem) => void,
  setIsAgentTurn: (v: boolean) => void,
  sourceEvent: AxonEvent | undefined,
  turnFlushedRef: MutableRefObject<boolean>,
  interruptedRef: MutableRefObject<boolean>,
  turnLiveRef: MutableRefObject<boolean>,
  eventTimestamp: number,
): void {
  const eventType = data.type as string;

  switch (eventType) {
    case 'turn.started':
      setIsAgentTurn(true);
      turnLiveRef.current = true;
      turnFlushedRef.current = false;
      interruptedRef.current = false;
      break;

    // The agent picked its last turn back up on its own (a background task
    // finished). The turn is live again, but this is not a fresh turn: blocks
    // held open by pending background tasks stay in the live buffer, and a
    // turn that was already flushed keeps its flushed marker so the resumed
    // content merges into that message when the resumed turn finishes.
    case 'turn.resumed':
      setIsAgentTurn(true);
      turnLiveRef.current = true;
      break;

    // The broker's between-turns background-task set. Non-empty means the
    // agent is still working after its turn ended, so keep the turn live;
    // empty means the background work is done, so end it the way a normal
    // turn end does, flushing whatever the pending tasks held open.
    case 'background.changed': {
      const active = Array.isArray(data.active) ? data.active : [];
      if (active.length > 0) {
        setIsAgentTurn(true);
        break;
      }
      // The broker publishes this only between turns; one that lands while
      // a turn is live is out of order and must not flush the turn under it.
      if (turnLiveRef.current) break;
      // Same finalization as the Claude `result` path: fold in the result
      // metadata parked while background tasks were pending, and honour a
      // user interrupt over the agent's native stop reason.
      const pending = blocks.pendingResultRef.current;
      blocks.pendingResultRef.current = null;
      const interrupted = interruptedRef.current;
      if (interrupted) {
        blocks.updateBlocks((prev) => cancelInFlightTools(prev, eventTimestamp));
      }
      const flushed = blocks.flushToMessage(
        {
          stopReason: interrupted ? 'cancelled' : (pending?.stopReason ?? 'end_turn'),
          cost: pending?.cost || undefined,
          numTurns: pending?.numTurns || undefined,
          durationMs: pending?.durationMs || undefined,
          timestamp: eventTimestamp,
          sourceEvent,
        },
        eventTimestamp,
      );
      if (flushed) appendMessage(flushed);
      setIsAgentTurn(false);
      turnFlushedRef.current = true;
      break;
    }

    case 'turn.completed':
    case 'turn.failed': {
      // The broker follows a native Codex `turn/completed` with its normalized
      // system terminal. Child-thread native terminals are absorbed into their
      // background-agent card, so consume the paired system event too instead
      // of flushing or ending the still-running parent turn.
      if (eventType === 'turn.completed' && blocks.codexSubagentTerminalsRef.current > 0) {
        blocks.codexSubagentTerminalsRef.current -= 1;
        break;
      }
      // The turn is over even when pending background tasks keep its blocks
      // open below: the broker's next `background.changed` may end it.
      turnLiveRef.current = false;
      if (eventType === 'turn.failed') {
        const errorText = (data.error as string) ?? (data.message as string) ?? null;
        if (errorText && blocks.blocksRef.current.length === 0) {
          blocks.pushBlock({
            type: 'text',
            id: nextBlockId('err'),
            text: errorText,
            messageId: null,
            sourceEvent,
          });
        }
      }
      // Don't flush if there are pending background tasks — keep blocks live
      // so the completion turn's content merges into the same message.
      if (eventType !== 'turn.failed' && blocks.hasPendingBackgroundTasks()) {
        break;
      }
      // A user interrupt re-frames the turn as cancelled regardless of the
      // agent's native stop reason (e.g. OpenCode reports `end_turn` even when
      // the prompt was cancelled).
      const interrupted = interruptedRef.current;
      if (interrupted) {
        blocks.updateBlocks((prev) => cancelInFlightTools(prev, eventTimestamp));
      }
      const stopReason = interrupted
        ? 'cancelled'
        : eventType === 'turn.failed'
          ? 'error'
          : ((data.stop_reason as string) ?? (data.stopReason as string) ?? 'end_turn');
      const flushed = blocks.flushToMessage(
        {
          stopReason,
          isError: eventType === 'turn.failed' && !interrupted,
          timestamp: eventTimestamp,
          sourceEvent,
        },
        eventTimestamp,
      );
      if (flushed) appendMessage(flushed);
      setIsAgentTurn(false);
      turnFlushedRef.current = true;
      break;
    }

    case 'turn.cancelled': {
      // The agent's own cancelled turn-end (Codex). The agent stops mid-stream,
      // so in-progress tool calls never receive a terminal tool_call_update —
      // mark them cancelled here so they stop showing "Running…" with a
      // spinner. Then flush the in-flight blocks into a finished assistant
      // message with a "Cancelled" badge.
      turnLiveRef.current = false;
      interruptedRef.current = true;
      blocks.updateBlocks((prev) => cancelInFlightTools(prev, eventTimestamp));
      const flushed = blocks.flushToMessage(
        {
          stopReason: 'cancelled',
          timestamp: eventTimestamp,
          sourceEvent,
        },
        eventTimestamp,
      );
      if (flushed) appendMessage(flushed);
      setIsAgentTurn(false);
      turnFlushedRef.current = true;
      break;
    }

    case 'broker.error': {
      const flushed = blocks.flushToMessage(
        {
          isError: true,
          stopReason: (data.message as string) ?? 'broker error',
          timestamp: eventTimestamp,
          sourceEvent,
        },
        eventTimestamp,
      );
      if (flushed) appendMessage(flushed);
      setIsAgentTurn(false);
      break;
    }

    case 'devbox.running':
    case 'devbox.suspended':
    case 'devbox.shutdown':
    case 'devbox.failed':
    case 'agent.error': {
      if (sourceEvent) {
        const lifecycleItem = buildAgentLifecycleItem(sourceEvent);
        if (lifecycleItem) {
          // Broker publishes stderr lines as agent.error with errorType: "stderr".
          // Show as muted log text, not error cards — don't end the agent turn.
          const logLine = brokerLogLine(sourceEvent);
          if (logLine !== null) {
            lifecycleItem.eventType = 'agent.stderr';
            lifecycleItem.label = logLine;
            if (appendAgentLogBlock(logLine, blocks, sourceEvent)) break;
          }
          lifecycleItem.sourceEvent = sourceEvent;
          const flushed = blocks.flushToMessage(undefined, eventTimestamp);
          if (flushed) appendMessage(flushed);
          appendMessage(lifecycleItem);
          if (
            logLine === null &&
            (eventType === 'devbox.failed' ||
              eventType === 'devbox.suspended' ||
              eventType === 'devbox.shutdown' ||
              eventType === 'agent.error')
          ) {
            setIsAgentTurn(false);
          }
        }
      }
      break;
    }

    // Flush before noting the new model: `flushToMessage` stamps the turn it
    // commits with the noted model, so work the old model wrote keeps its
    // label. A mid-turn switch splits the bubble in two.
    case 'agent.runtime_settings_changed': {
      const change = runtimeSettingsSwitch(data);
      // Racing switches announce out of post order; the record kept the later
      // post, so an announcement below the watermark changes nothing.
      if (change && !blocks.acceptsSwitch(change.sequence)) break;
      const flushed = blocks.flushToMessage(undefined, eventTimestamp);
      if (flushed) appendMessage(flushed);
      if (change) blocks.noteSwitch(change);
      // An older typed Codex switch has no `command` text and already has its
      // own `codex.command.result` row, so it gets no divider.
      if (data.source === 'command' && data.command === undefined) break;
      if (sourceEvent) {
        const lifecycleItem = buildAgentLifecycleItem(sourceEvent);
        if (lifecycleItem) {
          lifecycleItem.sourceEvent = sourceEvent;
          appendMessage(lifecycleItem);
        }
      }
      break;
    }

    case 'agent.source.flow':
    case 'agent.flow_result':
    case 'agent.daemon_started':
    case 'agent.performance_warning':
    case 'agent.work_assessment':
    case 'agent.capability_requested':
    case 'agent.capability_resolved':
    case 'agent.credentials_refreshed':
    case 'agent.credentials_refresh_failed':
    case 'agent.pr_created':
    case 'agent.pr_merged':
    case 'agent.pr_closed':
    case 'agent.pr_checks_failed':
    case 'agent.pr_review': {
      // Passive informational banners — they don't END the agent turn (a daemon
      // can come up and a usage warning can trip mid-work), so we never flip
      // `isAgentTurn` off the way error/devbox events do. But they MUST render
      // below the work that triggered them: the live turn renders after the
      // committed `messages`, so appending the banner without flushing would
      // float it above the still-streaming turn. Flush the in-flight blocks
      // into a committed assistant message first (committing the work above the
      // banner); the turn keeps going, so later content forms a new bubble
      // below. SDKChatView's agent_lifecycle branch renders each banner as its
      // own card (flow deep-link, daemon "Open in browser", performance warning).
      if (sourceEvent) {
        const lifecycleItem = buildAgentLifecycleItem(sourceEvent);
        if (lifecycleItem) {
          lifecycleItem.sourceEvent = sourceEvent;
          const flushed = blocks.flushToMessage(undefined, eventTimestamp);
          if (flushed) appendMessage(flushed);
          appendMessage(lifecycleItem);
        }
      }
      break;
    }
  }
}
